/**
 * YMM result page regrouping (owner pick B 14/10, business/Shopify#52). Loaded by sections/spo-page-content.liquid on
 * /pages/product-result only.
 *
 * The C: Year Make Model app writes its result list into #products (20, then more via its own #ymm_load_more).
 * This script waits for that list, asks the app for its remaining pages, then redraws the same products as:
 *   1. "Listed for your 2018 Toyota Hilux (N)"  (vehicle-specific), or the "No parts are listed" state with
 *      "Ask us with your rego or VIN" when there are none;
 *   2. "Universal parts (N)"  (tag fit:universal or fitment.summary_make "Universal Fit", from the card view):
 *      always below the vehicle's parts (or below the empty state), 4 cards then "Show all N" (#52 item 6).
 * Cards are the shared product card (/products/{handle}?view=spo-card). If anything fails the app's own list stays.
 * Reads the vehicle from ?rq= (and the app's ymm_labels cookie for nicer names); never writes cookies.
 *
 * Owner 14/10 (#52 item 6e): vehicle matching follows ODOO's fitment data everywhere. Alongside the app's list this
 * reads the store-wide fit index (sections/spo-fit-index via /collections/all?section_id=spo-fit-index, 250 per
 * page; Odoo's fitment.summary_make_model_year) and adds the non-universal parts Odoo lists for the vehicle that the
 * app's list misses (e.g. OXF418 x 6 for a 2018 Hilux) to the vehicle group, after the app's own matches. Same match
 * rule as the collection view and the card fit lines (window.spoVehicle.fits, snippets/spo-vehicle-js.liquid).
 * root[data-spo-ymm-added] = how many were added. If the index can't be read, the app's list is used alone.
 *
 * Build 7 (owner 14/10, #52/#54): "Universal parts (N)" comes from ODOO, not the app. Every product in the fit index
 * flagged universal (tag fit:universal or fitment.summary_make "Universal Fit") is listed, available first; the
 * app's own universal results (its "Universal Products" tab) are skipped completely, so the group is complete even
 * when that tab is empty. De-duplicated: a part already in the vehicle group is not listed again. 4 cards first,
 * the rest are fetched on "Show all N". root[data-spo-ymm-uni-odoo] = universal parts from the index,
 * root[data-spo-ymm-uni-app-skipped] = app results skipped as universal. Index unreadable = the app's universal
 * results as before. Test: ?spo_ymm_no_app_uni=1 also drops the app's universal results before anything else
 * (same output expected).
 */
(() => {
  const root = document.querySelector('[data-spo-ymm]');
  if (!root) return;

  const nice = (s) => String(s || '').split(/[-_\s]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');
  const cookie = (name) => {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
    if (!hit) return '';
    try {
      return decodeURIComponent(hit.slice(name.length + 1));
    } catch (e) {
      return hit.slice(name.length + 1);
    }
  };

  /** The searched vehicle as "2018 Toyota Hilux" (rq first, labels from the app's cookie when they match). */
  const vehicleLabel = () => {
    const rq = new URLSearchParams(location.search).get('rq') || '';
    const parts = (rq || cookie('ymm_values')).split(/__|~/).filter(Boolean);
    const labels = cookie('ymm_labels').split('~');
    const sameAsCookie = cookie('ymm_values').split('~').join('__') === parts.join('__');
    const out = [];
    for (const [i, part] of parts.entries()) {
      const at = part.indexOf('_');
      if (at < 1) continue;
      const key = part.slice(0, at);
      if (!['yr', 'mk', 'md', 'sr', 'vr'].includes(key)) continue;
      out.push(sameAsCookie && labels[i] ? labels[i].trim() : key === 'yr' ? part.slice(at + 1) : nice(part.slice(at + 1)));
    }
    return out.join(' ');
  };

  const label = vehicleLabel();
  for (const n of root.querySelectorAll('[data-spo-ymm-vehicle]')) n.textContent = label || 'vehicle';
  const ask = root.querySelector('[data-spo-ymm-ask]');
  if (ask && label) {
    const u = new URL(ask.getAttribute('href') || '/pages/contact-us', location.origin);
    u.searchParams.set('part', `Parts for my ${label}`);
    ask.setAttribute('href', u.pathname + u.search);
  }

  const change = root.querySelector('[data-spo-ymm-change]');
  change?.addEventListener('click', () => {
    const open = document.documentElement.classList.toggle('spo-ymm-search-open');
    change.setAttribute('aria-expanded', String(open));
    change.textContent = open ? 'Hide vehicle search' : 'Change vehicle';
    if (open) setTimeout(() => document.querySelector('#ymm_searchbox_cont .ss-main')?.focus(), 50);
  });

  const cardCache = new Map();
  const fetchCard = async (handle) => {
    if (cardCache.has(handle)) return cardCache.get(handle);
    const res = await fetch(`/products/${encodeURIComponent(handle)}?view=spo-card`);
    if (!res.ok) throw new Error(`card ${handle}: ${res.status}`);
    const li = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('li.spo-coll__item');
    if (!li) throw new Error(`card ${handle}: no item`);
    cardCache.set(handle, li);
    return li;
  };
  const fetchCards = async (handles) => {
    const out = new Array(handles.length);
    let next = 0;
    const worker = async () => {
      while (next < handles.length) {
        const i = next++;
        try {
          out[i] = await fetchCard(handles[i]);
        } catch (e) {
          out[i] = null;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, handles.length) }, worker));
    return out;
  };

  const UNI_FIRST = 4;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /**
   * From the store-wide fit index (Odoo's data): fit = non-universal parts Odoo lists for the searched vehicle;
   * uni = every universal part, available first (index order otherwise). null if the index can't be read.
   */
  const odooHandles = async () => {
    const helper = window.spoVehicle;
    const vehicle = helper?.current?.();
    const canFit = vehicle && typeof helper.fits === 'function';
    const fit = [];
    const uniIn = [];
    const uniOut = [];
    for (let page = 1; page < 20; page++) {
      const res = await fetch(`/collections/all?section_id=spo-fit-index&page=${page}`);
      if (!res.ok) throw new Error(`fit index ${page}: ${res.status}`);
      const node = new DOMParser().parseFromString(await res.text(), 'text/html').querySelector('[data-spo-fit-index]');
      if (!node) {
        if (page === 1) throw new Error('fit index: none');
        break;
      }
      for (const [handle, available, universal, summary] of JSON.parse(node.textContent)) {
        if (universal) (available ? uniIn : uniOut).push(handle);
        else if (canFit && helper.fits(summary, vehicle)) fit.push(handle);
      }
      if (page >= (parseInt(node.dataset.pages, 10) || 1)) break;
    }
    return { fit, uni: [...uniIn, ...uniOut] };
  };

  /** Wait for the app's list, pull its remaining pages, return the product handles in the app's order. */
  const appHandles = async () => {
    const deadline = Date.now() + 20000;
    let lastCount = -1;
    let stableSince = Date.now();
    while (Date.now() < deadline) {
      const items = document.querySelectorAll('#products .ymm-grid-item');
      const totalText = document.getElementById('total_products')?.textContent || '';
      const total = parseInt(totalText.replace(/\D/g, ''), 10);
      const more = document.getElementById('ymm_load_more');
      const cur = parseInt(more?.dataset.currPage || '1', 10);
      const pages = parseInt(more?.dataset.totalPages || '1', 10);
      if (items.length !== lastCount) {
        lastCount = items.length;
        stableSince = Date.now();
      }
      const known = totalText.trim() !== '';
      if (known && total === 0) return [];
      if (known && items.length >= total && total > 0) break;
      if (known && items.length > 0 && cur < pages && Date.now() - stableSince > 400) {
        more.click();
        stableSince = Date.now();
      }
      if (known && items.length > 0 && cur >= pages && Date.now() - stableSince > 1500) break;
      await sleep(250);
    }
    const handles = [];
    for (const a of document.querySelectorAll('#products .ymm-grid-item .product-thumb a[href*="/products/"]')) {
      const h = new URL(a.getAttribute('href'), location.origin).pathname.split('/products/')[1]?.split('/')[0];
      if (h && !handles.includes(h)) handles.push(decodeURIComponent(h));
    }
    return handles;
  };

  const run = async () => {
    if (!new URLSearchParams(location.search).get('rq')) return; // no search yet: the app's search box only
    root.hidden = false;
    const status = root.querySelector('[data-spo-ymm-status]');
    const odoo = odooHandles().catch(() => null);
    let handles;
    try {
      handles = await appHandles();
    } catch (e) {
      root.hidden = true;
      return;
    }
    const index = await odoo;
    const added = (index ? index.fit : []).filter((h) => !handles.includes(h));
    let cards = await fetchCards(handles);
    if (handles.length && cards.every((c) => !c)) {
      root.hidden = true;
      return;
    }
    if (new URLSearchParams(location.search).get('spo_ymm_no_app_uni') === '1') {
      // Test switch: behave as if the app had returned no universal parts at all.
      cards = cards.filter((c) => c && c.dataset.universal !== 'true');
    }
    const fit = [];
    const uni = [];
    let appUniSkipped = 0;
    for (const card of cards) {
      if (!card) continue;
      const li = card.cloneNode(true);
      if (li.dataset.universal !== 'true') fit.push(li);
      else if (index) appUniSkipped += 1; // the universal group comes from Odoo's index below
      else uni.push(li); // index unreadable: the app's universal results, as before
    }
    root.dataset.spoYmmUniAppSkipped = String(appUniSkipped);
    let addedCount = 0;
    for (const card of await fetchCards(added)) {
      if (!card) continue;
      const li = card.cloneNode(true);
      li.dataset.spoOdooMatch = 'true';
      fit.push(li);
      addedCount += 1;
    }
    root.dataset.spoYmmAdded = String(addedCount);
    // Sold-out parts last within each group, keeping the app's order otherwise.
    const order = (list) => [...list.filter((l) => l.dataset.available === 'true'), ...list.filter((l) => l.dataset.available !== 'true')];

    const fitBox = root.querySelector('[data-spo-ymm-fit]');
    const uniBox = root.querySelector('[data-spo-ymm-uni]');
    const empty = root.querySelector('[data-spo-ymm-empty]');
    if (fit.length) {
      root.querySelector('[data-spo-ymm-fit-grid]').replaceChildren(...order(fit));
      root.querySelector('[data-spo-ymm-fit-n]').textContent = `(${fit.length})`;
      fitBox.hidden = false;
      empty.hidden = true;
    } else {
      fitBox.hidden = true;
      empty.hidden = false;
    }
    // Universal group below the vehicle's parts (#52 item 6, owner 14/10): 4 cards, then "Show all N".
    // From Odoo's index (build 7), minus anything already in the vehicle group; the rest load on "Show all".
    const shown = new Set(fit.map((li) => li.dataset.handle || li.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('/products/')[1]?.split(/[?#/]/)[0]));
    const uniHandles = index ? index.uni.filter((h) => !shown.has(h)) : [];
    root.dataset.spoYmmUniOdoo = String(uniHandles.length);
    const uniTotal = index ? uniHandles.length : uni.length;
    const uniGrid = root.querySelector('[data-spo-ymm-uni-grid]');
    const more = root.querySelector('[data-spo-ymm-uni-more]');
    const button = root.querySelector('[data-spo-ymm-uni-all]');
    const clones = (list) => list.filter(Boolean).map((c) => c.cloneNode(true));
    if (uniTotal) {
      const first = index ? clones(await fetchCards(uniHandles.slice(0, UNI_FIRST))) : order(uni).slice(0, UNI_FIRST);
      uniGrid.replaceChildren(...first);
      root.querySelector('[data-spo-ymm-uni-n]').textContent = `(${uniTotal})`;
      if (more && button && uniTotal > UNI_FIRST) {
        button.textContent = `Show all ${uniTotal}`;
        more.hidden = false;
        button.addEventListener('click', async () => {
          button.setAttribute('aria-busy', 'true');
          const rest = index ? clones(await fetchCards(uniHandles.slice(UNI_FIRST))) : order(uni).slice(UNI_FIRST);
          uniGrid.append(...rest);
          more.hidden = true;
          rest[0]?.querySelector('a[href]')?.focus();
        }, { once: true });
      }
      uniBox.hidden = false;
    } else {
      uniBox.hidden = true;
    }
    status.textContent = fit.length
      ? `${fit.length} part${fit.length === 1 ? '' : 's'} listed for your ${label}${uniTotal ? `, ${uniTotal} universal` : ''}.`
      : `No parts are listed for your ${label}${uniTotal ? `. ${uniTotal} universal parts suit most vehicles` : ''}.`;
    status.classList.add('visually-hidden');
    document.documentElement.classList.add('spo-ymm-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
})();
